import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { DashboardSummary } from "@/types";
import { formatLeaveType } from "@/utils/leaveType";
import {
  Briefcase,
  Calendar,
  CheckCircle2,
  Clock,
  FileText,
  UserMinus,
  Users,
  XCircle,
} from "lucide-react";
import { LeaveBalanceCard } from "./LeaveBalanceCard";

interface SummarySectionProps {
  data?: DashboardSummary;
  isLoading: boolean;
}

export function SummarySection({ data, isLoading }: SummarySectionProps) {
  if (isLoading) {
    return (
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {[...Array(8)].map((_, i) => (
          <Card key={i} className="animate-pulse">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <div className="h-4 w-24 bg-muted rounded" />
              <div className="h-4 w-4 bg-muted rounded" />
            </CardHeader>
            <CardContent>
              <div className="h-8 w-12 bg-muted rounded" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (!data) {
    return (
      <Card className="border-dashed">
        <CardContent className="flex flex-col items-center justify-center p-6 text-muted-foreground">
          <Briefcase className="h-8 w-8 mb-2 opacity-50" />
          <p>No summary data available</p>
        </CardContent>
      </Card>
    );
  }

  const stats = [
    {
      label: "Total Employees",
      value: data.totalEmployees || 0,
      icon: Users,
      color: "text-blue-500",
    },
    {
      label: "Active Employees",
      value: data.activeEmployees || 0,
      icon: CheckCircle2,
      color: "text-green-500",
    },
    {
      label: "Deleted Employees",
      value: data.deletedEmployees || 0,
      icon: UserMinus,
      color: "text-red-500",
    },
    {
      label: "Total Holidays",
      value: data.totalHolidays || 0,
      icon: Calendar,
      color: "text-purple-500",
    },
    {
      label: "Total Leave Requests",
      value: data.totalLeaveRequests || 0,
      icon: FileText,
      color: "text-orange-500",
    },
    {
      label: "Pending Approvals",
      value: data.pendingApprovals || 0,
      icon: Clock,
      color: "text-yellow-500",
    },
    {
      label: "Approved Leaves",
      value: data.approvedLeaves || 0,
      icon: CheckCircle2,
      color: "text-emerald-500",
    },
    {
      label: "Rejected Leaves",
      value: data.rejectedLeaves || 0,
      icon: XCircle,
      color: "text-rose-500",
    },
  ];

  return (
    <div className="space-y-6">
      {/* Stats Grid */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {stats.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <Card key={index} className="shadow-sm">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">
                  {stat.label}
                </CardTitle>
                <Icon className={`h-4 w-4 ${stat.color}`} />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stat.value}</div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {/* Upcoming Holidays */}
        <Card className="col-span-1 shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Calendar className="h-5 w-5 text-primary" />
              Upcoming Holidays
            </CardTitle>
          </CardHeader>
          <CardContent>
            {data.upcomingHolidays.length > 0 ? (
              <div className="space-y-4">
                {data.upcomingHolidays.map((holiday) => (
                  <div
                    key={holiday.id}
                    className="flex items-center justify-between border-b last:border-0 pb-2 last:pb-0"
                  >
                    <span className="font-medium text-sm">{holiday.name}</span>
                    <Badge variant="outline" className="text-xs">
                      {new Date(holiday.date).toLocaleDateString()}
                    </Badge>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground text-center py-4">
                No upcoming holidays
              </p>
            )}
          </CardContent>
        </Card>

        {/* Team On Leave Today */}
        <Card className="col-span-1 shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <UserMinus className="h-5 w-5 text-warning" />
              On Leave Today
            </CardTitle>
          </CardHeader>
          <CardContent>
            {data.teamOnLeaveToday.length > 0 ? (
              <div className="space-y-4">
                {data.teamOnLeaveToday.map((leave, i) => (
                  <div
                    key={i}
                    className="flex flex-col space-y-1 border-b last:border-0 pb-2 last:pb-0"
                  >
                    <div className="flex justify-between items-center">
                      <span className="font-medium text-sm">{leave.name}</span>
                      <Badge variant="secondary" className="text-xs">
                        {formatLeaveType(leave.leaveType)}
                      </Badge>
                    </div>
                    <span className="text-xs text-muted-foreground">
                      {new Date(leave.startDate).toLocaleDateString()} -{" "}
                      {new Date(leave.endDate).toLocaleDateString()}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground text-center py-4">
                No one is on leave today
              </p>
            )}
          </CardContent>
        </Card>

        {/* Recent Leave Requests */}
        <Card className="col-span-1 md:col-span-2 lg:col-span-1 shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <FileText className="h-5 w-5 text-blue-500" />
              Recent Requests
            </CardTitle>
          </CardHeader>
          <CardContent>
            {data.recentLeaveRequests && data.recentLeaveRequests.length > 0 ? (
              <div className="space-y-4">
                {data.recentLeaveRequests.map((request) => (
                  <div
                    key={request.id}
                    className="flex flex-col space-y-1 border-b last:border-0 pb-2 last:pb-0"
                  >
                    <div className="flex justify-between items-center">
                      <span className="font-medium text-sm">
                        {request.employeeName}
                      </span>
                      <Badge
                        variant={
                          request.status === "PENDING"
                            ? "outline"
                            : request.status === "APPROVED"
                              ? "default"
                              : "destructive"
                        }
                        className="text-xs"
                      >
                        {request.status === "PENDING"
                          ? "PENDING"
                          : request.status}
                      </Badge>
                    </div>
                    <div className="flex justify-between items-center text-xs text-muted-foreground">
                      <span>
                        {formatLeaveType(
                          request.type || (request as { leaveType?: string }).leaveType,
                        )}
                      </span>
                      <span>
                        {new Date(request.createdAt).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground text-center py-4">
                No recent requests
              </p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Leave Balances Section */}
      <LeaveBalanceCard balances={data.leaveBalances} />
    </div>
  );
}
